import {
  DEFAULT_FINANCE_THRESHOLDS,
  FINANCE_BAND_LABELS,
  financeBandRange,
  type FinanceBand,
  type FinanceThresholds,
} from '@ukinfo/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatCurrency } from '../lib/format';
import { Badge, cn } from './ui';

/**
 * Grenzen der Betragsklassen.
 * Sie aendern sich praktisch nie und werden deshalb lange zwischengespeichert.
 */
export function useFinanceThresholds(): FinanceThresholds {
  const { data } = useQuery({
    queryKey: ['settings', 'finance'],
    queryFn: () => api.get<FinanceThresholds>('/api/settings/finance'),
    staleTime: 15 * 60_000,
  });
  return data ?? { ...DEFAULT_FINANCE_THRESHOLDS };
}

const BAND_TONES = {
  GREEN: 'success',
  YELLOW: 'warning',
  ORANGE: 'orange',
} as const;

const BAND_DOTS: Record<FinanceBand, string> = {
  GREEN: 'bg-emerald-500',
  YELLOW: 'bg-amber-500',
  ORANGE: 'bg-orange-500',
};

/**
 * Betrag eines Sponsorvertrags.
 *
 * Wer die Zahl sehen darf, bekommt sie (`amount` ist dann gesetzt). Alle
 * anderen sehen die Groessenklasse - der Vertrag bleibt damit einordenbar,
 * ohne die Summe preiszugeben. Welche Variante gilt, entscheidet der Server;
 * hier wird nur dargestellt, was angekommen ist.
 */
export function Amount({
  amount,
  band,
  currency = 'EUR',
  className,
}: {
  amount: number | null;
  band: FinanceBand;
  currency?: string;
  className?: string;
}): JSX.Element {
  const thresholds = useFinanceThresholds();

  if (amount !== null) {
    return (
      <span className={cn('font-semibold tabular-nums text-slate-800', className)}>
        {formatCurrency(amount, currency)}
      </span>
    );
  }

  return (
    <Badge
      tone={BAND_TONES[band]}
      className={className}
      title={`${FINANCE_BAND_LABELS[band]} (${financeBandRange(band, thresholds)}) – der genaue Betrag ist für deine Funktion nicht freigegeben.`}
    >
      <span className={cn('h-2 w-2 rounded-full', BAND_DOTS[band])} aria-hidden="true" />
      {FINANCE_BAND_LABELS[band]}
    </Badge>
  );
}
